import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { 
  DocumentTextIcon,
  ArrowPathIcon,
  PlayIcon,
  PauseIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  ClockIcon,
  ServerIcon,
  CubeIcon
} from '@heroicons/react/24/outline';
import { dockerAPI } from '../services/api';

const DockerLogs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedService, setSelectedService] = useState('');
  const [selectedContainer, setSelectedContainer] = useState('');
  const [logFilter, setLogFilter] = useState('');
  const [maxLines, setMaxLines] = useState(500);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5000);
  const [logLevel, setLogLevel] = useState('all');
  const [showTimestamps, setShowTimestamps] = useState(true);
  const logsEndRef = useRef(null);

  // Get initial selection from URL params
  useEffect(() => {
    const serviceParam = searchParams.get('service');
    const containerParam = searchParams.get('container');
    if (serviceParam) setSelectedService(serviceParam);
    if (containerParam) setSelectedContainer(containerParam);
  }, [searchParams]);

  // Queries para obter dados
  const { data: servicesData } = useQuery(
    'docker-services',
    () => dockerAPI.getServices(),
    { refetchInterval: 15000 }
  );

  const { data: containersData } = useQuery(
    'docker-containers',
    dockerAPI.getContainers,
    { refetchInterval: 15000 }
  );

  const { data: logsData, isLoading: loadingLogs, refetch: refetchLogs } = useQuery(
    ['docker-logs', selectedService || selectedContainer, maxLines],
    () => {
      if (selectedService) {
        return dockerAPI.getServiceLogs(selectedService, { lines: maxLines });
      } else if (selectedContainer) {
        return dockerAPI.getContainerLogs(selectedContainer, { lines: maxLines });
      }
      return null;
    },
    {
      enabled: !!(selectedService || selectedContainer),
      refetchInterval: autoRefresh ? refreshInterval : false,
      onSuccess: () => {
        // Auto-scroll to bottom when new logs arrive
        setTimeout(() => {
          logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  );

  const services = servicesData?.data?.services || [];
  const containers = containersData?.data?.containers || [];
  const logs = logsData?.data?.logs || [];

  // Filter logs based on search and level
  const filteredLogs = logs.filter(log => {
    const logText = typeof log === 'string' ? log : log.message || '';
    const matchesFilter = !logFilter || logText.toLowerCase().includes(logFilter.toLowerCase());
    
    if (logLevel === 'all') return matchesFilter;
    
    const logLowerCase = logText.toLowerCase();
    switch (logLevel) {
      case 'error':
        return matchesFilter && (logLowerCase.includes('error') || logLowerCase.includes('err') || logLowerCase.includes('fatal'));
      case 'warn':
        return matchesFilter && (logLowerCase.includes('warn') || logLowerCase.includes('warning'));
      case 'info':
        return matchesFilter && (logLowerCase.includes('info') || logLowerCase.includes('notice'));
      case 'debug':
        return matchesFilter && (logLowerCase.includes('debug') || logLowerCase.includes('trace'));
      default:
        return matchesFilter;
    }
  });

  const formatLogLine = (log) => {
    const logText = typeof log === 'string' ? log : log.message || '';
    
    // Extract timestamp if present
    const timestampMatch = logText.match(/^(\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?)/);
    const timestamp = timestampMatch ? timestampMatch[1] : '';
    const message = timestampMatch ? logText.substring(timestampMatch[0].length).trim() : logText;
    
    // Determine log level color (optimized for dark background)
    const messageLower = message.toLowerCase();
    let colorClass = 'text-gray-100';
    if (messageLower.includes('error') || messageLower.includes('fatal') || messageLower.includes('err')) {
      colorClass = 'text-red-400';
    } else if (messageLower.includes('warn') || messageLower.includes('warning')) {
      colorClass = 'text-yellow-300';
    } else if (messageLower.includes('info') || messageLower.includes('notice')) {
      colorClass = 'text-blue-300';
    } else if (messageLower.includes('debug') || messageLower.includes('trace')) {
      colorClass = 'text-gray-400';
    }

    return { timestamp, message, colorClass };
  };

  const handleSelectionChange = (type, value) => {
    if (type === 'service') {
      setSelectedService(value);
      setSelectedContainer('');
      if (value) {
        setSearchParams({ service: value });
      } else {
        setSearchParams({});
      }
    } else {
      setSelectedContainer(value);
      setSelectedService('');
      if (value) {
        setSearchParams({ container: value });
      } else {
        setSearchParams({});
      }
    }
  };

  const clearLogs = () => {
    // Since we can't actually clear Docker logs, we'll just refetch
    refetchLogs();
  };

  const getSelectedName = () => {
    if (selectedService) {
      const service = services.find(s => s.id === selectedService || s.name === selectedService);
      return service?.name || selectedService;
    }
    if (selectedContainer) {
      const container = containers.find(c => c.id === selectedContainer || c.name === selectedContainer);
      return container?.name || selectedContainer;
    }
    return 'Nenhum selecionado';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Logs Docker</h1>
        <p className="mt-1 text-sm text-gray-500">
          Visualize logs em tempo real de serviços e containers Docker
        </p>
      </div>

      {/* Controls */}
      <div className="card">
        <div className="space-y-4">
          {/* Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <ServerIcon className="h-4 w-4 inline mr-1" />
                Selecionar Serviço:
              </label>
              <select
                value={selectedService}
                onChange={(e) => handleSelectionChange('service', e.target.value)}
                className="input w-full"
              >
                <option value="">Escolher serviço...</option>
                {services.map((service) => (
                  <option key={service.id || service.name} value={service.id || service.name}>
                    {service.name} ({service.replicas} réplicas)
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <CubeIcon className="h-4 w-4 inline mr-1" />
                Selecionar Container:
              </label>
              <select
                value={selectedContainer}
                onChange={(e) => handleSelectionChange('container', e.target.value)}
                className="input w-full"
              >
                <option value="">Escolher container...</option>
                {containers.map((container) => (
                  <option key={container.id} value={container.id}>
                    {container.name} ({container.status})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Filters and Controls */}
          <div className="border-t pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MagnifyingGlassIcon className="h-4 w-4 inline mr-1" />
                  Filtrar logs:
                </label>
                <input
                  type="text"
                  value={logFilter}
                  onChange={(e) => setLogFilter(e.target.value)}
                  placeholder="Buscar no conteúdo..."
                  className="input w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <AdjustmentsHorizontalIcon className="h-4 w-4 inline mr-1" />
                  Nível:
                </label>
                <select
                  value={logLevel}
                  onChange={(e) => setLogLevel(e.target.value)}
                  className="input w-full"
                >
                  <option value="all">Todos os níveis</option>
                  <option value="error">Erros</option>
                  <option value="warn">Avisos</option>
                  <option value="info">Informações</option>
                  <option value="debug">Debug</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Máx. linhas:
                </label>
                <select
                  value={maxLines}
                  onChange={(e) => setMaxLines(parseInt(e.target.value))}
                  className="input w-full"
                >
                  <option value={100}>100 linhas</option>
                  <option value={500}>500 linhas</option>
                  <option value={1000}>1000 linhas</option>
                  <option value={2000}>2000 linhas</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <ClockIcon className="h-4 w-4 inline mr-1" />
                  Atualização:
                </label>
                <select
                  value={refreshInterval}
                  onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
                  className="input w-full"
                >
                  <option value={2000}>2 segundos</option>
                  <option value={5000}>5 segundos</option>
                  <option value={10000}>10 segundos</option>
                  <option value={30000}>30 segundos</option>
                </select>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={`btn-secondary flex items-center space-x-2 ${autoRefresh ? 'bg-green-50 text-green-700' : ''}`}
                >
                  {autoRefresh ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
                  <span>{autoRefresh ? 'Pausar' : 'Retomar'}</span>
                </button>
                
                <button
                  onClick={() => setShowTimestamps(!showTimestamps)}
                  className={`btn-secondary ${showTimestamps ? 'bg-blue-50 text-blue-700' : ''}`}
                >
                  Timestamps
                </button>
                
                <button
                  onClick={refetchLogs}
                  disabled={loadingLogs}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${loadingLogs ? 'animate-spin' : ''}`} />
                  <span>Atualizar</span>
                </button>
                
                <button
                  onClick={clearLogs}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <TrashIcon className="h-4 w-4" />
                  <span>Limpar</span>
                </button>
              </div>
              
              <div className="text-sm text-gray-600">
                {filteredLogs.length} de {logs.length} linhas | {getSelectedName()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Logs Display */}
      <div className="card">
        <div className="bg-gray-900 rounded-lg overflow-hidden">
          <div className="bg-gray-800 px-4 py-2 text-white text-sm border-b border-gray-700">
            <div className="flex items-center justify-between">
              <span className="flex items-center space-x-2">
                <DocumentTextIcon className="h-4 w-4" />
                <span>Console Output</span>
              </span>
              {autoRefresh && (
                <span className="text-green-400 text-xs flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span>Tempo real</span>
                </span>
              )}
            </div>
          </div>
          
          <div className="h-96 overflow-y-auto font-mono text-sm bg-gray-900 text-gray-100 p-4">
            {!selectedService && !selectedContainer ? (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <DocumentTextIcon className="mx-auto h-12 w-12 mb-4" />
                  <p>Selecione um serviço ou container para ver os logs</p>
                </div>
              </div>
            ) : loadingLogs ? (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <ArrowPathIcon className="mx-auto h-8 w-8 mb-2 animate-spin" />
                  <p>Carregando logs...</p>
                </div>
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <DocumentTextIcon className="mx-auto h-12 w-12 mb-4" />
                  <p>Nenhum log encontrado</p>
                  <p className="text-sm mt-1">Tente ajustar os filtros ou aguardar novos logs</p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                {filteredLogs.map((log, index) => {
                  const { timestamp, message, colorClass } = formatLogLine(log);
                  return (
                    <div key={index} className="flex space-x-3 hover:bg-gray-800 px-2 py-1 rounded">
                      {showTimestamps && timestamp && (
                        <span className="text-gray-500 text-xs whitespace-nowrap mt-0.5">
                          {timestamp}
                        </span>
                      )}
                      <span className={`${colorClass} break-all flex-1`}>
                        {message}
                      </span>
                    </div>
                  );
                })}
                <div ref={logsEndRef} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DockerLogs;